/**
 * This component is also used by our native apps.
 * The currentProperty data will not be set for this case.
 * We fetch listing details if that is the case to get pricing information.
 *
 * ex:
 *  /rental-manager/properties/{listingAlias}/learn-more-paid-inclusion?hostApp=android
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Flex,
    IconChecklistOutline,
    IconDollarSignCircleOutline,
    IconYardSignOutline,
    Image,
    Spacer,
    Text,
} from '@zillow/constellation';

import accessUtils from 'app/shared/utils/accessUtils';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';

// actions
import PostingActions from 'app/shared/actions/PostingActions';

// selectors
import { PropertiesSelectors, RoutingSelectors } from 'app/shared/selectors';

// assets
import zBluePersonCircle from 'app/shared/images/z-blue-person-circle.svg';

import { StyledModalSection } from 'app/shared/containers/ModalContainer/StyledComponents';

import {
    StyledCenterText,
    StyledDivider,
    StyledFeatureList,
    StyledIcon,
    StyledListItem,
} from './StyledComponents';

// TODO: after paidInclusion.properties is added to response:
// update default price
const defaultPrice = '$9.99';

const LeadGuarantee = () => {
    // selectors
    const { appConfig, cookieEnabledServices } = useSelector(state => state.app);
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    // required vars before hooks
    const { listingAlias, leadGuarantee, paidInclusion } = currentProperty || {};

    // TODO: after paidInclusion.properties is added to response:
    // price will no longer exist
    const { isFlatFeeEnabled, price: paidInclusionPrice = '', products = []} = paidInclusion || {};
    const hasCurrentPropertyData = listingAlias !== '';

    // TODO: remove after we fully release feature
    const canAccessFlatFee = accessUtils.canAccessFlatFee({ isFlatFeeEnabled, appConfig, cookieEnabledServices });

    // hooks
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(!hasCurrentPropertyData);
    const [price, setPrice] = useState(defaultPrice);
    const [productList, setProductList] = useState(products);

    useEffect(() => {
        // check price from redux store
        if (!hasCurrentPropertyData) {
            setIsLoading(true);

            // no price set - fetch price
            // fetch via listingId as this value comes from the Router
            dispatch(PostingActions.property.getDetails(listingId))
                .then((response) => {
                    if (canAccessFlatFee) {
                        setProductList(response.paidInclusion.products);
                    }
                })
                .catch(() => {
                    // error when fetching - set price to default price
                    setPrice(defaultPrice);
                }).finally(() => {
                    setIsLoading(false);
                });
        } else {
            // use value from redux store
            setPrice(paidInclusionPrice);
            setIsLoading(false);
        }
    }, [listingAlias]);

    const { isEligible: isEligibleForLeadGuarantee } = leadGuarantee;

    // TODO: remove after we fully release feature
    if (canAccessFlatFee && productList.length === 0) {
        console.info('INFO: you have the flat fee feature enabled, but not using mock data.');
        console.info('INFO: add query param &useFlatFeeMockData=true');
        console.info('INFO: the pricing information will be the default value', defaultPrice);
    }

    const renderPayPerWeekContent = () => (
        <Text type="body" data-test="content-pay-per-week">
                Post your&nbsp;
            <b>first listing for free</b>
                ,&nbsp;and pay only&nbsp;
            <b>{`${price}/week`}</b>
                &nbsp;for additional listings.
        </Text>
    );

    const renderFlatFeeContent = () => {
        const productPayPerWeek = productList[0];
        const productPayFlatFee = productList[1];
        const displayPayPerWeekDuration = `${productPayPerWeek.price}/${productPayPerWeek.paymentValidDuration}`;
        const displayPayFlatFeeDuration = `${productPayFlatFee.price} for ${productPayFlatFee.paymentValidDuration}`;

        return (
            <Text fontType="body" data-test="content-flat-fee">
                    Post once&nbsp;
                <b>for free</b>
                    ,&nbsp;and pay either&nbsp;
                <b>{displayPayPerWeekDuration}</b>
                    &nbsp;or&nbsp;
                <b>{displayPayFlatFeeDuration}</b>
                    &nbsp;for additional listings.
            </Text>
        );
    };

    const renderLeadGuaranteeBanner = () => {
        const heading = 'List with confidence.';
        const body = 'If you don\'t receive a lead within a week, it\'s free to list until you do (for up to 8 weeks).';
        const actionLabel = 'Learn more about our Lead Guarantee';
        const actionPath = 'https://help.zillowrentalmanager.com/hc/en-us/articles/360048462014';

        return (
            <LeadGuaranteeBanner
                heading={heading}
                body={body}
                actionLabel={actionLabel}
                actionPath={actionPath}
                actionTarget="_blank"
                iconWidthPX="60px"
                paddingXLOnDesktop={true}
            />
        );
    };

    if (isLoading) {
        return (
            <div className="LeadGuarantee">
                <SpinnerContainer />
            </div>
        );
    }

    return (
        <div className="LeadGuarantee">
            <Spacer marginBottom="sm">
                <StyledModalSection>
                    <Spacer className="PIVPM-ImageContainer" marginBottom="md">
                        <Flex
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                        >
                            <Image src={zBluePersonCircle} alt="background" />
                        </Flex>
                    </Spacer>

                    <Spacer className="PIVPM-content">
                        <Spacer marginBottom="xs">
                            <StyledCenterText as="div" fontType="h6">
                            The hassle-free way to find renters fast
                            </StyledCenterText>
                        </Spacer>

                        <StyledCenterText as="div" fontType="body">
                    Fill your vacancies, screen renters and more with Zillow Rental Manager
                        </StyledCenterText>
                    </Spacer>

                    <Spacer className="PIVPM-divider" marginY="md">
                        <Flex
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                        >
                            <StyledDivider />
                        </Flex>
                    </Spacer>

                    <Spacer className="PIVPM-ListContainer" marginBottom="xl">
                        <StyledFeatureList>
                            <StyledListItem>
                                <StyledIcon>
                                    <IconYardSignOutline />
                                </StyledIcon>
                                <Text type="body">
                            List once and reach renters on Zillow, Trulia and HotPads.
                                </Text>
                            </StyledListItem>

                            <StyledListItem>
                                <StyledIcon>
                                    <IconChecklistOutline />
                                </StyledIcon>
                                <Text type="body">
                            Screen renters online with criminal background checks and credit reports.
                                </Text>
                            </StyledListItem>

                            <StyledListItem>
                                <StyledIcon>
                                    <IconDollarSignCircleOutline />
                                </StyledIcon>
                                {canAccessFlatFee && productList.length > 0
                                    ? renderFlatFeeContent()
                                    : renderPayPerWeekContent()
                                }
                            </StyledListItem>
                        </StyledFeatureList>
                    </Spacer>
                </StyledModalSection>
            </Spacer>

            {!canAccessFlatFee && isEligibleForLeadGuarantee && (
                <Spacer className="PIVPM-BannerContainer">
                    {renderLeadGuaranteeBanner()}
                </Spacer>
            )}
        </div>
    );
};

LeadGuarantee.propTypes = {};

LeadGuarantee.defaultProps = {};

export default LeadGuarantee;
